import { Component, signal, afterNextRender, inject, effect, OnInit, ChangeDetectionStrategy, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterOutlet } from '@angular/router';
import { AuthService } from './core/auth/services/auth.service';
import { SessionMonitorService } from './core/auth/services/session-monitor.service';
import { PushService } from './core/services/push-service';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ConfirmDialog } from './shared/ui/confirm-dialog/confirm-dialog';
import { VisibilidadeService } from './core/services/visibilidade.service';
import { AppService } from './app.service'; 
import { GeolocationService } from './core/geolocation/services/geolocation.service';
import { SessaoService, SalvarConfigNotificacaoSessaoRequest } from './core/services/sessao.service';
import { NotificationPreferencesService } from './core/notifications/services/notification-preferences.service';
import { filterToBackendRecognizedTypes } from './core/notifications/models/notification.model';
import { AppUpdateService } from './core/services/app-update.service';
import { PwaInstallService } from './core/services/pwa-install.service';
import { PwaInstallDialog } from './core/services/pwa-install-dialog';
import { PwaIosInstallBanner } from './core/services/pwa-ios-install-banner';
import { GuarnicaoStore } from './core/guarnicao/services/guarnicao.store';
import { ThemeService } from './core/theme/theme.service';
import { EMPTY, catchError, exhaustMap, filter, fromEvent, merge, timer } from 'rxjs';
import { FeatureFlagService } from './core/feature-flags';
import { TelemetriaService } from './core/geolocation/services/telemetria.service';
import { GeolocationResult } from './core/geolocation/models/geolocation.model';

import { DIALOG_PRESETS } from './shared/utils/dialog-presets';
@Component({
  selector: 'app-root',
  imports: [RouterOutlet, MatDialogModule, PwaIosInstallBanner],
  templateUrl: './app.html',
  styleUrl: './app.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class App implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly sessionMonitor = inject(SessionMonitorService);
  private readonly pushService = inject(PushService);
  private readonly dialog = inject(MatDialog);
  private readonly visibilidadeService = inject(VisibilidadeService);
  private readonly service = inject(AppService);
  private readonly geoService = inject(GeolocationService);
  private readonly telemetria = inject(TelemetriaService);
  private readonly featureFlags = inject(FeatureFlagService);
  private readonly sessaoService = inject(SessaoService);
  private readonly notificationPrefs = inject(NotificationPreferencesService);
  private readonly appUpdateService = inject(AppUpdateService);
  protected readonly pwaInstallService = inject(PwaInstallService);
  private readonly guarnicaoStore = inject(GuarnicaoStore);
  private readonly destroyRef = inject(DestroyRef);
  // Injetar ThemeService no root garante que o tema seja aplicado antes da renderização
  private readonly _themeService = inject(ThemeService);
  protected readonly title = signal('nexus-app');

  rastreavel = false;
  private lastSessionFetchUsername: string | null = null;

  constructor() {
    effect(() => {
      const user = this.authService.user();
      
      if (user) {
        const username = user.preferredUsername || user.matricula;
        if (username && this.lastSessionFetchUsername !== username) {
          this.lastSessionFetchUsername = username;
          this.hydrateAndSyncNotifications(user.cpf);
        }
        
        this.sessionMonitor.startMonitoring();
      }
    });
    
    afterNextRender(() => {
      this.removeSplashScreen();
      if (
        !this.pushService.getSubscription() &&
        typeof Notification !== 'undefined' &&
        Notification.permission !== 'granted'
      ) {
        setTimeout(() => {
          this.requestNotificationPermission();
        }, 1000);
      }

      setTimeout(() => this.checkPwaInstall(), 3000);
    });
  }

  ngOnInit(): void {
    this.appUpdateService.initialize();
    this.pwaInstallService.initialize();
    this.handleLocalizaoSessaoUsuario();
    // Carrega a guarnição do IndexedDB no startup para que o fallback do
    // computed `equipe` nos steps do RAP funcione independente da rota inicial.
    this.guarnicaoStore.carregarInicial();
  }

  private handleLocalizaoSessaoUsuario(): void {
    this.visibilidadeService.iniciar();
    merge(timer(0, 30000), fromEvent(document, 'visibilitychange'), fromEvent(window, 'online'))
      .pipe(
        filter(() => !!this.authService.user() && document.visibilityState === 'visible' && navigator.onLine),
        exhaustMap(() => {
          const cpf = this.authService.user()?.cpf;
          return this.geoService.getCurrentPosition({ highAccuracy: true, timeout: 15000, maximumAge: 0 })
            .pipe(filter(() => this.authService.user()?.cpf === cpf), catchError(() => { this.rastreavel = false; this.telemetria.erro.set('Não foi possível obter o GPS. Confira a permissão de localização.'); return EMPTY; }));
        }),
        filter(() => !!this.authService.user()),
        takeUntilDestroyed(this.destroyRef),
      ).subscribe(result => this.atualizarPosicao(result, this.service.getDeviceId(), navigator.userAgent));
  }

  private atualizarPosicao(
    result: GeolocationResult,
    deviceId: string,
    plataforma: string
  ): void {
    const position = {
      lat: result.coordenadas.latitude,
      lng: result.coordenadas.longitude
    };

    this.rastreavel = true;
    this.service.setPosicao(position);
    if (this.featureFlags.isEnabled('viaturaServico')) {
      this.telemetria.enviar(result, deviceId).pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({ error: () => undefined });
    }

    this.service
      .salvaSessao(
        String(position.lat),
        String(position.lng),
        deviceId,
        plataforma
      )
      .subscribe({
        error: (error) => {
          if (error?.status !== 404) {
            console.warn('[App] Não foi possível sincronizar a posição da sessão:', error?.message || error);
          }
        }
      });
  }

  requestNotificationPermission(): void {
    this.dialog.open(ConfirmDialog, { ...DIALOG_PRESETS.formCompact, 
      data: {
        titulo: 'Notificações',
        mensagem: 'Deseja ativar as notificações?',
        textoBotaoConfirmar: 'Sim',
        textoBotaoCancelar: 'Não',
        tipo: 'info',
        onConfirm: () => {
          this.pushService.enableNotifications();
        }
      }
    });
  }

  private checkPwaInstall(): void {
    if (this.pwaInstallService.isInstalled()) return;

    this.pwaInstallService.evaluateShowDialog();

    if (this.pwaInstallService.shouldShowInstallDialog()) {
      if (this.pwaInstallService.isIosSafari()) {
        // iOS: banner com instruções é exibido via template
      } else if (this.pwaInstallService.canInstall()) {
        this.dialog.open(PwaInstallDialog, { ...DIALOG_PRESETS.formCompact, 
          width: '340px',
          disableClose: false,
        });
      }
    }
  }

  private hydrateAndSyncNotifications(cpf: string): void {
    this.sessaoService.buscarConfigNotificacao(cpf).subscribe({
      next: (result) => {
        if (result?.configuracao) {
          this.notificationPrefs.hydrateFromBackend(result.configuracao);
        }
        this.syncNotificationConfig();
        this.pushService.checkAndRenewSubscription();
      },
      error: () => {
        this.syncNotificationConfig();
        this.pushService.checkAndRenewSubscription();
      },
    });
  }

  private syncNotificationConfig(): void {
    const config = this.notificationPrefs.config();
    const prefs = this.notificationPrefs.pushPreferences();

    const payload: SalvarConfigNotificacaoSessaoRequest = {
      matricula: this.authService.user()?.matricula ?? '',
      contexto: 'NEXUS_WEB',
      salvoEm: new Date().toISOString(),
      configuracao: {
        habilitado: prefs.pushEnabled,
        raioKm: config.radiusKm,
        tiposAlerta: filterToBackendRecognizedTypes(prefs.enabledTypes as unknown as string[]),
      },
    };

    this.sessaoService.salvarConfigNotificacao(payload).subscribe({
      error: (error) => console.error('Erro ao sincronizar config de notificação:', error),
    });
  }

  private removeSplashScreen(): void {
    const splashScreen = document.getElementById('pmdf-splash-screen');
    if (!splashScreen) return;

    const animationDuration = 2000;
    
    setTimeout(() => {
      splashScreen.style.opacity = '0';
      splashScreen.style.transition = 'opacity 0.5s ease-out';
      
      setTimeout(() => {
        splashScreen.remove();
      }, 500);
    }, animationDuration);
  }
}


export interface Position {
  lat: string | number | null;
  lng: string | number | null;
}
